/**
 * v2 Locations list + LocationDetail.
 *
 * Mirrors v1's Gigafy-portal-driven location list:
 *   - GET /api/gigafy/locations            — list with chassis counts
 *   - GET /api/gigafy/locations/{id}/devices — devices at a location
 *   - POST /api/gigafy/locations/{id}/sync   — re-fetch the device list
 *   - POST /api/chassis/adopt                — adopt one device as a chassis
 *
 * The "default-on filter" hides locations with zero discovered
 * chassis so the operator sees only the operational sites first;
 * toggle re-shows the long tail.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw, Search, Smartphone } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { api, asApiError } from "../../api/client";
import { Badge, type BadgeTone } from "../components/Badge";
import { CopyableCell } from "../components/CopyableCell";
import { useToast } from "../components/Toast";
import {
  VdslErrorCountsPills,
  type VdslErrorHealthCounts,
} from "../components/VdslErrorCountsPills";
import { HelpButton } from "../help/HelpPanel";
import { desktopOptedOut, isSmallScreen } from "../mobile/MobileShell";

type SuggestedMatch = {
  entity_id: string;
  name: string | null;
  address: string | null;
};

type Location = {
  entity_id: string;
  name: string | null;
  address: string | null;
  region: string | null;
  device_state: number | null;
  chassis_total: number | null;
  chassis_managed: number | null;
  is_placeholder: boolean;
  suggested_match: SuggestedMatch | null;
};

type LocationsResponse = { locations: Location[] };

type SiteProvisionSummary = {
  gigafy_location_id: string;
  adapter_key: string;
  auto_provision_enabled: boolean;
  boost_active: boolean;
};
type SiteProvisionListResponse = { sites: SiteProvisionSummary[] };

function adoptionTone(total: number, managed: number): BadgeTone {
  if (total === 0) return "muted";
  if (managed === total) return "success";
  if (managed === 0) return "warning";
  return "info";
}

export function Locations() {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  // v0.32.0 — phones land on the field-tech view unless they've used
  // the mobile shell's "Desktop" escape (localStorage opt-out).
  useEffect(() => {
    if (isSmallScreen() && !desktopOptedOut()) {
      navigate("/m", { replace: true });
    }
  }, [navigate]);
  const [filter, setFilter] = useState("");
  const [hideEmpty, setHideEmpty] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addState, setAddState] = useState("");

  const q = useQuery<LocationsResponse>({
    queryKey: ["v2", "locations"],
    queryFn: async () => {
      const res = await api.get<LocationsResponse>("/gigafy/locations");
      return res.data;
    },
    staleTime: 60_000,
  });

  // Read-only auto-provision indicator per site — one cheap list-all
  // query for the whole page. A site is "on" when ANY adapter row for
  // it has the toggle enabled; "boosted" wins the label.
  const autoProvQ = useQuery<SiteProvisionListResponse>({
    queryKey: ["v2", "site-provision-defaults"],
    queryFn: async () => {
      const res = await api.get<SiteProvisionListResponse>(
        "/locations/provision-defaults",
      );
      return res.data;
    },
    staleTime: 60_000,
  });
  const autoProvByLocation = useMemo(() => {
    const m = new Map<string, { enabled: boolean; boosted: boolean }>();
    for (const s of autoProvQ.data?.sites ?? []) {
      const cur = m.get(s.gigafy_location_id) ?? {
        enabled: false,
        boosted: false,
      };
      m.set(s.gigafy_location_id, {
        enabled: cur.enabled || s.auto_provision_enabled,
        boosted:
          cur.boosted || (s.auto_provision_enabled && s.boost_active),
      });
    }
    return m;
  }, [autoProvQ.data]);

  const createPlaceholder = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { name: addName.trim() };
      const state = addState.trim();
      if (state) body.state = state;
      await api.post("/gigafy/locations/placeholder", body);
    },
    onSuccess: () => {
      toast.push("Placeholder location added", "success");
      qc.invalidateQueries({ queryKey: ["v2", "locations"] });
      setAddName("");
      setAddState("");
      setAddOpen(false);
    },
    onError: (err: unknown) =>
      toast.push(asApiError(err).detail || "Could not add location", "error"),
  });

  const mergePlaceholder = useMutation({
    mutationFn: async (v: { placeholderId: string; targetId: string }) => {
      await api.post(`/gigafy/locations/${v.placeholderId}/merge`, {
        target_location_id: v.targetId,
      });
    },
    onSuccess: () => {
      toast.push("Merged into the Gigafy location", "success");
      qc.invalidateQueries({ queryKey: ["v2", "locations"] });
    },
    onError: (err: unknown) =>
      toast.push(asApiError(err).detail || "Merge failed", "error"),
  });

  const filtered = useMemo(() => {
    const all = q.data?.locations ?? [];
    const needle = filter.trim().toLowerCase();
    const searching = needle.length > 0;
    return all
      .filter((l) => {
        // When the operator is searching by name, ignore hideEmpty —
        // an unactivated location has chassis_total = null (we've never
        // cached its devices) so it'd be hidden by the default filter
        // and impossible to find. Letting a search pierce the filter
        // means "Local South Melbourne" surfaces even before its first
        // sync.
        // Placeholders are operator-created stand-ins — never hide them,
        // even before their first chassis is added.
        if (
          hideEmpty &&
          !searching &&
          !l.is_placeholder &&
          (l.chassis_total ?? 0) === 0
        )
          return false;
        if (!needle) return true;
        return (
          (l.name ?? "").toLowerCase().includes(needle) ||
          (l.address ?? "").toLowerCase().includes(needle) ||
          (l.region ?? "").toLowerCase().includes(needle)
        );
      })
      .sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? ""),
      );
  }, [q.data, filter, hideEmpty]);

  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-baseline justify-between gap-2 animate-enter">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-slate-100">Locations</h1>
          <HelpButton topic="locations" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs tabular-nums text-slate-500">
            {filtered.length} shown · {q.data?.locations.length ?? 0} total
          </span>
          <Link
            to="/m"
            className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-300 hover:border-emerald-700 hover:text-slate-100"
            title="Phone-first view: locations → device → ports with refresh buttons"
          >
            <Smartphone className="h-3.5 w-3.5" />
            Mobile view
          </Link>
          <button
            type="button"
            onClick={() => setAddOpen((v) => !v)}
            className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-100 hover:border-emerald-700 hover:bg-emerald-950/30"
          >
            <Plus className="h-3.5 w-3.5" />
            Add location
          </button>
        </div>
      </header>
      {addOpen && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (addName.trim()) createPlaceholder.mutate();
          }}
          className="flex flex-wrap items-end gap-2 rounded-lg border border-emerald-900/60 bg-emerald-950/10 p-3"
        >
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wide text-slate-400">
              Location name
            </label>
            <input
              autoFocus
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              placeholder="e.g. Local South Melbourne"
              className="w-72 rounded border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100 placeholder:text-slate-500 focus-visible:border-emerald-700 focus-visible:outline-none"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wide text-slate-400">
              State
            </label>
            <input
              value={addState}
              onChange={(e) => setAddState(e.target.value)}
              placeholder="e.g. VIC"
              className="w-24 rounded border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100 placeholder:text-slate-500 focus-visible:border-emerald-700 focus-visible:outline-none"
            />
          </div>
          <button
            type="submit"
            disabled={!addName.trim() || createPlaceholder.isPending}
            className="rounded border border-emerald-700 bg-emerald-900/40 px-3 py-1.5 text-sm text-emerald-100 hover:bg-emerald-900/70 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {createPlaceholder.isPending ? "Adding…" : "Add"}
          </button>
          <p className="w-full text-[11px] text-slate-500">
            Use this for a Gigafy site that isn't activated in the portal yet
            (so it doesn't appear in the list). You can add chassis to it now;
            once Gigafy starts returning the real site, a “merge” prompt appears
            here to reconcile them.
          </p>
        </form>
      )}
      <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-900 shadow-border p-3 animate-enter [animation-delay:80ms]">
        <div className="relative flex-1 min-w-[20rem]">
          <Search className="pointer-events-none absolute left-2 top-2 h-3.5 w-3.5 text-slate-500" />
          <input
            type="search"
            placeholder="Filter by name, address, or region…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full rounded border border-slate-700 bg-slate-950 py-1.5 pl-7 pr-2 text-sm text-slate-100 placeholder:text-slate-500 focus-visible:border-emerald-700 focus-visible:outline-none"
          />
        </div>
        <label className="inline-flex cursor-pointer items-center gap-1 text-xs text-slate-300">
          <input
            type="checkbox"
            checked={hideEmpty}
            onChange={(e) => setHideEmpty(e.target.checked)}
          />
          hide locations with no chassis
        </label>
      </div>
      {q.isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-slate-500">
          {q.data?.locations.length === 0
            ? "No Gigafy locations cached yet — run gigafy_service_sync."
            : "No locations match the filter."}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 animate-enter [animation-delay:160ms]">
          {filtered.map((l) => {
            const total = l.chassis_total ?? 0;
            const managed = l.chassis_managed ?? 0;
            const autoProv = autoProvByLocation.get(l.entity_id);
            return (
              <Link
                key={l.entity_id}
                to={`/locations/${l.entity_id}`}
                className="group flex flex-col gap-1.5 rounded-lg bg-slate-900 shadow-border p-3 transition-[box-shadow,background-color] duration-150 ease-out hover:bg-emerald-950/10 hover:shadow-border-hover"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-medium text-slate-100 group-hover:text-emerald-200">
                    {l.name ?? `Location ${l.entity_id.slice(0, 8)}…`}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    {autoProv?.enabled && (
                      <Badge
                        tone={autoProv.boosted ? "warning" : "success"}
                        title="Pending ONUs at this site are provisioned automatically from the saved site template"
                      >
                        {autoProv.boosted ? "auto ⚡" : "auto"}
                      </Badge>
                    )}
                    {total > 0 && (
                      <Badge tone={adoptionTone(total, managed)}>
                        {managed}/{total} chassis
                      </Badge>
                    )}
                  </span>
                </div>
                {l.address && (
                  <div className="text-xs text-slate-500">{l.address}</div>
                )}
                {l.region && (
                  <div className="text-[10px] text-slate-600">{l.region}</div>
                )}
                {l.is_placeholder && (
                  <div className="mt-0.5">
                    <Badge tone="warning">Placeholder</Badge>
                  </div>
                )}
                {l.suggested_match && (
                  <button
                    type="button"
                    onClick={(e) => {
                      // The card is a <Link>; don't navigate on merge.
                      e.preventDefault();
                      e.stopPropagation();
                      if (
                        window.confirm(
                          `Merge this placeholder into the Gigafy location ` +
                            `“${l.suggested_match?.name ?? ""}”? Its chassis ` +
                            `will move to the real location.`,
                        )
                      ) {
                        mergePlaceholder.mutate({
                          placeholderId: l.entity_id,
                          targetId: l.suggested_match!.entity_id,
                        });
                      }
                    }}
                    disabled={mergePlaceholder.isPending}
                    className="mt-1 rounded border border-emerald-700 bg-emerald-900/40 px-2 py-1 text-[11px] text-emerald-100 hover:bg-emerald-900/70 disabled:opacity-50"
                  >
                    Gigafy now lists this site — merge & reconcile
                  </button>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ----------------------------------------------------------------------
// Location detail — devices at one location, adopt-button per row
// ----------------------------------------------------------------------

type DeviceOut = {
  entity_id: string;
  name: string | null;
  description: string | null;
  ip_address: string | null;
  make: string | null;
  model: string | null;
  device_role_name: string | null;
  state: number | null;
  vendor_hint: string | null;
  technology_hint: string | null;
  adoption_tag: "managed" | "unmanaged-recognised" | "unmanaged-unknown";
  chassis_id: number | null;
  health_counts: HealthCounts | null;
};

type HealthCounts = {
  good: number;
  warning: number;
  critical: number;
  unknown: number;
  total: number;
  // VDSL chassis only — line error-health flags (migration 0036).
  vdsl_errors?: VdslErrorHealthCounts | null;
};

const TAG_TONE: Record<DeviceOut["adoption_tag"], BadgeTone> = {
  managed: "success",
  "unmanaged-recognised": "info",
  "unmanaged-unknown": "muted",
};

// Tailwind dot colours kept in sync with v1's opticalHealth.ts
// dotColourClass(). Backend (apps/web/app/routers/gigafy.py)
// owns the bucketing — same thresholds as that module — so we
// only need the colour mapping here.
type HealthBandLabels = {
  emptyText: string;
  noScanText: string;
  goodTooltip: string;
  warningTooltip: string;
  criticalTooltip: string;
  unknownTooltip: string;
};

const GPON_LABELS: HealthBandLabels = {
  emptyText: "no ONUs scanned",
  noScanText: "no recent optical readings",
  goodTooltip: "ONUs with healthy RX power (-25 to -10 dBm)",
  warningTooltip: "ONUs in the marginal RX band (-27..-25 or -10..-8 dBm)",
  criticalTooltip: "ONUs out of band (> -8 or < -27 dBm)",
  unknownTooltip: "ONUs in inventory with no recent optical reading",
};

const VDSL_LABELS: HealthBandLabels = {
  emptyText: "no VDSL ports scanned",
  noScanText: "no recent line-speed samples",
  goodTooltip: "VDSL ports with downstream rate above 80 Mbps",
  warningTooltip: "VDSL ports in 40–80 Mbps range",
  criticalTooltip: "VDSL ports below 40 Mbps",
  unknownTooltip: "VDSL ports with no current line-speed sample",
};

function HealthPills({
  counts,
  technology,
}: {
  counts: HealthCounts;
  technology: string | null;
}) {
  const labels = technology === "vdsl" ? VDSL_LABELS : GPON_LABELS;
  if (counts.total === 0) {
    return <span className="text-[10px] text-slate-500">{labels.emptyText}</span>;
  }
  // If every entry is in "unknown" the chassis hasn't been scanned
  // recently — surface that explicitly rather than showing four zeros.
  if (counts.unknown === counts.total) {
    return (
      <span
        className="text-[10px] text-slate-500"
        title="No samples in the last 7 days"
      >
        {labels.noScanText} ({counts.total})
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 text-[11px]">
      <span
        className="inline-flex items-center gap-1 text-emerald-400"
        title={labels.goodTooltip}
      >
        <span aria-hidden>●</span>
        <span className="text-slate-200">{counts.good}</span>
      </span>
      <span
        className="inline-flex items-center gap-1 text-amber-400"
        title={labels.warningTooltip}
      >
        <span aria-hidden>●</span>
        <span className="text-slate-200">{counts.warning}</span>
      </span>
      <span
        className="inline-flex items-center gap-1 text-red-500"
        title={labels.criticalTooltip}
      >
        <span aria-hidden>●</span>
        <span className="text-slate-200">{counts.critical}</span>
      </span>
      {counts.unknown > 0 ? (
        <span
          className="inline-flex items-center gap-1 text-slate-500"
          title={labels.unknownTooltip}
        >
          <span aria-hidden>●</span>
          <span className="text-slate-300">{counts.unknown}</span>
        </span>
      ) : null}
    </span>
  );
}

type ManualAddDraft = {
  name: string;
  vendor: string;
  technology: string;
  host: string;
  ssh_port: string;
};

const EMPTY_MANUAL_DRAFT: ManualAddDraft = {
  name: "",
  vendor: "calix",
  technology: "gpon",
  host: "",
  ssh_port: "",
};

const VENDOR_OPTIONS = ["radisys", "nokia", "huawei", "ubiquiti", "calix"];
const TECHNOLOGY_OPTIONS = ["gpon", "vdsl"];

export function LocationDetail() {
  const { locationId } = useParams<{ locationId: string }>();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [manualOpen, setManualOpen] = useState(false);
  const [draft, setDraft] = useState<ManualAddDraft>(EMPTY_MANUAL_DRAFT);

  const devices = useQuery<{ devices: DeviceOut[] }>({
    queryKey: ["v2", "location-devices", locationId],
    queryFn: async () => {
      const res = await api.get<{ devices: DeviceOut[] }>(
        `/gigafy/locations/${locationId}/devices`,
      );
      return res.data;
    },
    enabled: !!locationId,
  });

  const sync = useMutation({
    mutationFn: async () => {
      await api.post(`/gigafy/locations/${locationId}/sync`);
    },
    onSuccess: () => {
      toast.push("Re-fetched location from Gigafy", "success");
      qc.invalidateQueries({ queryKey: ["v2", "location-devices", locationId] });
      qc.invalidateQueries({ queryKey: ["v2", "locations"] });
    },
    onError: () => toast.push("Sync failed", "error"),
  });

  const adopt = useMutation({
    mutationFn: async (device: DeviceOut) => {
      if (!device.vendor_hint || !device.technology_hint) {
        throw new Error("Device missing vendor / technology hint");
      }
      const res = await api.post<{ id: number }>("/chassis/adopt", {
        gigafy_device_id: device.entity_id,
        vendor: device.vendor_hint,
        technology: device.technology_hint,
      });
      return res.data;
    },
    onSuccess: (data) => {
      toast.push(`Adopted as chassis-${data.id}`, "success");
      qc.invalidateQueries({ queryKey: ["v2", "location-devices", locationId] });
      qc.invalidateQueries({ queryKey: ["v2", "chassis-list"] });
    },
    onError: (err: Error) =>
      toast.push(err.message ?? "Adopt failed", "error"),
  });

  const manualAdd = useMutation({
    mutationFn: async () => {
      if (!locationId) throw new Error("missing location");
      const body: Record<string, unknown> = {
        gigafy_location_id: locationId,
        name: draft.name.trim(),
        vendor: draft.vendor,
        technology: draft.technology,
        host: draft.host.trim(),
      };
      const port = draft.ssh_port.trim();
      if (port) body.ssh_port = Number(port);
      const res = await api.post<{ id: number }>(
        "/chassis/manual-add",
        body,
      );
      return res.data;
    },
    onSuccess: (data) => {
      toast.push(`Manually added as chassis-${data.id}`, "success");
      qc.invalidateQueries({ queryKey: ["v2", "location-devices", locationId] });
      qc.invalidateQueries({ queryKey: ["v2", "chassis-list"] });
      setDraft(EMPTY_MANUAL_DRAFT);
      setManualOpen(false);
      navigate(`/chassis/${data.id}`);
    },
    onError: (err: Error) =>
      toast.push(err.message ?? "Manual add failed", "error"),
  });

  const manualValid =
    draft.name.trim().length > 0 && draft.host.trim().length > 0;

  const rows = devices.data?.devices ?? [];

  return (
    <section className="flex flex-col gap-3">
      <Link
        to="/locations"
        className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200"
      >
        ← All locations
      </Link>
      <header className="flex items-baseline justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-slate-100">Devices</h1>
          <HelpButton topic="location-detail" />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setManualOpen((o) => !o)}
            className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700"
            title="Add a chassis that isn't tracked in the Gigafy portal"
          >
            <Plus className="h-3 w-3" />
            Manual add
          </button>
          <button
            type="button"
            onClick={() => sync.mutate()}
            disabled={sync.isPending}
            className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-50"
            title="Re-fetch the device list from the Gigafy portal"
          >
            <RefreshCw className="h-3 w-3" />
            {sync.isPending ? "Syncing…" : "Sync from Gigafy"}
          </button>
        </div>
      </header>
      {manualOpen ? (
        <div className="rounded-lg bg-slate-900 shadow-border p-3">
          <p className="mb-2 text-[11px] text-slate-500">
            Manually adopt a chassis that isn't in the Gigafy portal.
            You'll set the SSH username + password on the chassis page
            after creation.
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-6">
            <label className="flex flex-col gap-1 text-[11px] text-slate-400 sm:col-span-2">
              Name
              <input
                type="text"
                value={draft.name}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, name: e.target.value }))
                }
                placeholder="gig-vendor-site-role-1"
                className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-200"
              />
            </label>
            <label className="flex flex-col gap-1 text-[11px] text-slate-400">
              Vendor
              <select
                value={draft.vendor}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, vendor: e.target.value }))
                }
                className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-200"
              >
                {VENDOR_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-[11px] text-slate-400">
              Technology
              <select
                value={draft.technology}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, technology: e.target.value }))
                }
                className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-200"
              >
                {TECHNOLOGY_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-[11px] text-slate-400">
              Host (IP)
              <input
                type="text"
                value={draft.host}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, host: e.target.value }))
                }
                placeholder="10.0.0.1"
                className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-200"
              />
            </label>
            <label className="flex flex-col gap-1 text-[11px] text-slate-400">
              SSH port
              <input
                type="text"
                inputMode="numeric"
                value={draft.ssh_port}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, ssh_port: e.target.value }))
                }
                placeholder="22"
                className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-200"
              />
            </label>
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setManualOpen(false);
                setDraft(EMPTY_MANUAL_DRAFT);
              }}
              className="rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => manualAdd.mutate()}
              disabled={!manualValid || manualAdd.isPending}
              className="rounded border border-emerald-700 bg-emerald-900/40 px-2 py-1 text-xs text-emerald-200 hover:bg-emerald-900/60 disabled:opacity-50"
            >
              {manualAdd.isPending ? "Adding…" : "Create chassis"}
            </button>
          </div>
        </div>
      ) : null}
      <div className="rounded-lg bg-slate-900 shadow-border p-3">
        {devices.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-xs text-slate-500">
            No devices at this location. Sync to re-fetch from Gigafy.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1">Name</th>
                <th className="px-2 py-1">Role</th>
                <th className="px-2 py-1">Make / model</th>
                <th className="px-2 py-1">IP</th>
                <th className="px-2 py-1">Status</th>
                <th className="px-2 py-1">Health</th>
                <th className="px-2 py-1 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/40 text-slate-300">
              {rows.map((d) => (
                <tr key={d.entity_id}>
                  <td className="px-2 py-1.5">
                    <CopyableCell value={d.name} />
                  </td>
                  <td className="px-2 py-1.5 text-slate-400">
                    {d.device_role_name ?? "—"}
                  </td>
                  <td className="px-2 py-1.5 text-slate-400">
                    {[d.make, d.model].filter(Boolean).join(" ") || "—"}
                  </td>
                  <td className="px-2 py-1.5">
                    <CopyableCell value={d.ip_address} />
                  </td>
                  <td className="px-2 py-1.5">
                    <Badge tone={TAG_TONE[d.adoption_tag]}>
                      {d.adoption_tag.replace("-", " ")}
                    </Badge>
                  </td>
                  <td className="px-2 py-1.5">
                    {d.health_counts ? (
                      <span className="inline-flex flex-wrap items-center gap-2">
                        <HealthPills
                          counts={d.health_counts}
                          technology={d.technology_hint}
                        />
                        <VdslErrorCountsPills
                          counts={d.health_counts.vdsl_errors}
                        />
                      </span>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {d.chassis_id != null ? (
                      <Link
                        to={`/chassis/${d.chassis_id}`}
                        className="text-emerald-400 hover:text-emerald-300"
                      >
                        open chassis →
                      </Link>
                    ) : d.vendor_hint && d.technology_hint ? (
                      <button
                        type="button"
                        onClick={() => adopt.mutate(d)}
                        disabled={adopt.isPending}
                        className="inline-flex items-center gap-1 rounded border border-emerald-700 bg-emerald-900/40 px-2 py-0.5 text-[11px] text-emerald-200 hover:bg-emerald-900/60 disabled:opacity-50"
                        title={`Adopt as ${d.vendor_hint}:${d.technology_hint}`}
                      >
                        Adopt
                      </button>
                    ) : (
                      <span className="text-[10px] text-slate-600">
                        unsupported vendor
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
